import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PACKAGES, PLATFORMS, STATUSES } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const empty = {
  first_name: "", last_name: "", phone: "", email: "", country: "Türkiye",
  platform: "Twitch", package: "Popüler Paket", price: 0,
  start_date: new Date().toISOString().slice(0, 10),
  delivery_date: "",
  warranty_end_date: "",
  status: "BAŞLANDI",
  notes: "",
};

export default function CustomerFormPage({ mode = "create" }) {
  const nav = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === "edit" && id) {
      api.get(`/customers/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Yüklenemedi"));
    }
  }, [mode, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (mode === "edit") {
        await api.put(`/customers/${id}`, payload);
        toast.success("Müşteri güncellendi");
      } else {
        await api.post("/customers", payload);
        toast.success("Müşteri eklendi");
      }
      nav("/customers");
    } catch (e) {
      toast.error("Kaydedilemedi. Tüm alanları kontrol edin.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <Button variant="ghost" onClick={() => nav(-1)} data-testid="back-btn" size="sm" className="text-muted-foreground -ml-3">
        <ArrowLeft className="h-4 w-4 mr-2" /> Geri
      </Button>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{mode === "edit" ? "Düzenle" : "Yeni Kayıt"}</div>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">
          {mode === "edit" ? "Müşteriyi Düzenle" : "Yeni Müşteri Ekle"}
        </h1>
      </div>

      <Card className="p-6 lg:p-8">
        <form onSubmit={submit} className="space-y-6" data-testid="customer-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Ad">
              <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="input-first-name" />
            </Field>
            <Field label="Soyad">
              <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="input-last-name" />
            </Field>
            <Field label="Telefon">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="input-phone" placeholder="+90 5XX XXX XX XX" />
            </Field>
            <Field label="E-posta">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="input-email" />
            </Field>
            <Field label="Ülke">
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="input-country" />
            </Field>
            <Field label="Fiyat (₺)">
              <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} required data-testid="input-price" />
            </Field>

            <Field label="Platform">
              <Select value={form.platform} onValueChange={(v) => set("platform", v)}>
                <SelectTrigger data-testid="select-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Paket">
              <Select value={form.package} onValueChange={(v) => set("package", v)}>
                <SelectTrigger data-testid="select-package"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            <Field label="İş Başlama Tarihi">
              <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} required data-testid="input-start-date" />
            </Field>
            <Field label="Teslimat Tarihi">
              <Input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} required data-testid="input-delivery-date" />
            </Field>
            <Field label="Garanti Bitiş Tarihi">
              <Input type="date" value={form.warranty_end_date} onChange={(e) => set("warranty_end_date", e.target.value)} required data-testid="input-warranty-date" />
            </Field>
            <Field label="İş Durumu">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="İş Detayı / Notlar">
            <Textarea rows={5} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Bu müşteri için özel iş tanımı, referans linkler, teslim edilen dosyalar..." data-testid="input-notes" />
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="ghost" onClick={() => nav(-1)}>İptal</Button>
            <Button type="submit" disabled={busy} data-testid="save-customer-btn" className="rounded-full">
              <Save className="h-4 w-4 mr-2" />
              {busy ? "Kaydediliyor..." : (mode === "edit" ? "Değişiklikleri Kaydet" : "Müşteri Ekle")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
